import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { RoleMismatchPage } from "@/pages/RoleMismatchPage";
import { canAccess, getHomeRoute } from "@/lib/roleRouter";
import { consentApi } from "@/lib/api";
import {
  canAccessTenantRole,
  getUserTenantRole,
  isSuperAdminUser,
  normalizePath,
} from "@/lib/userRoutes";

const isSamePath = (left, right) => normalizePath(left) === normalizePath(right);

/**
 * NAV-SWEEP-1b (N7) — a role denial is a DESTINATION, not a disappearance.
 *
 * Every denial below used to `safeRedirect(home)` and only fall back to RoleMismatchPage when the
 * home route WAS the denied route — a condition that is almost never true, so the card was
 * effectively dead code and every wrong door silently teleported the user to their dashboard with no
 * explanation. That is what made the N1/N2 doors read as BROKEN BUTTONS rather than closed doors:
 * click, screen changes, nothing said. Rendering the card instead keeps her oriented (it names her
 * role, the page she asked for, and gives her a way home or a way to switch accounts) and turns the
 * whole class from a bug into a designed boundary.
 *
 * The URL is deliberately left ALONE — she stays on the address she asked for, so browser-back
 * returns to where she came from instead of to a redirect that would bounce her forward again.
 */
const denyAccess = (user, tenantRole) => <RoleMismatchPage user={user} currentRole={tenantRole} />;

const loadingScreen = (message) => (
  <div className="flex h-screen items-center justify-center bg-slate-950 text-slate-50">
    <div className="animate-pulse text-sm text-slate-400">{message}</div>
  </div>
);

function safeRedirect(to, currentPath, state = undefined) {
  const target = normalizePath(to);

  if (isSamePath(target, currentPath)) {
    return null;
  }

  return <Navigate to={target} replace state={state} />;
}

export function ProtectedRoute({
  children,
  superAdminOnly = false,
  allowedTenantRoles = [],
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, initializing, loading, isLoading } = useAuth();

  const currentPath = normalizePath(location.pathname);
  const tenantRole = getUserTenantRole(user);
  const authIsLoading = Boolean(initializing || loading || isLoading);

  const isConsentRoute = isSamePath(currentPath, "/consent");
  const isPrivacyRoute = isSamePath(currentPath, "/privacy");

  const consentQuery = useQuery({
    queryKey: ["protected-consent-status", user?.id],
    enabled:
      Boolean(user) &&
      tenantRole === "teacher" &&
      !isConsentRoute &&
      !isPrivacyRoute,
    queryFn: () => consentApi.status().then((res) => res.data),
    staleTime: 60_000,
    retry: 1,
  });

  if (authIsLoading) {
    return loadingScreen(t("protectedRoute.loadingWorkspace"));
  }

  if (!user) {
    if (isSamePath(currentPath, "/login")) {
      return children;
    }

    // NAV-SWEEP-1b (N3) — carry the FULL intended location, not just the pathname. A reviewer who
    // follows a deep link with a stale session is sent to /login; AuthPage now returns her to exactly
    // this location afterwards, so `?tab=preferences` / `?lesson=&t=` survive the round trip.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${currentPath}${location.search || ""}${location.hash || ""}` }}
      />
    );
  }

  if (superAdminOnly && !isSuperAdminUser(user)) {
    return denyAccess(user, tenantRole);
  }

  if (
    Array.isArray(allowedTenantRoles) &&
    allowedTenantRoles.length > 0 &&
    !canAccessTenantRole(user, allowedTenantRoles)
  ) {
    return denyAccess(user, tenantRole);
  }

  if (
    tenantRole === "teacher" &&
    !isConsentRoute &&
    !isPrivacyRoute &&
    !consentQuery.isLoading &&
    !consentQuery.isFetching &&
    consentQuery.data &&
    consentQuery.data.all_granted === false
  ) {
    const redirect = safeRedirect("/consent", currentPath, { from: currentPath });
    return redirect || children;
  }

  if (!canAccess(user, currentPath, allowedTenantRoles)) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("Role route mismatch", {
        role: tenantRole,
        attemptedPath: currentPath,
        homeRoute: getHomeRoute(user),
      });
    }

    return denyAccess(user, tenantRole);
  }

  // F-1 (UX-0): do NOT render the role-denial card here based on leftover `roleMismatch` navigation
  // state. Reaching this point means every access check above PASSED — the user is authorized for
  // this route. The earlier guards (superAdminOnly / allowedTenantRoles / canAccess) already deny +
  // redirect genuinely-unauthorized routes. Honoring stale redirect state here denied a teacher
  // their OWN home (/my-workspace) after a bounce off an admin route deposited the flag, and
  // RoleMismatchPage mislabeled the authorized path as "Requested page". An authorized route always
  // renders its children.
  return children;
}

export default ProtectedRoute;
